interface SlackMessage {
    text: string;
    blocks?: any[];
}

export const sendSlackNotification = async (message: SlackMessage) => {
    const webhookUrl = process.env.SLACK_WEBHOOK_URL;

    if (!webhookUrl) {
        console.warn(
            'SLACK_WEBHOOK_URL not configured, skipping Slack notification',
        );
        return;
    }

    try {
        const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(message),
        });

        if (!response.ok) {
            throw new Error(`Slack API error: ${response.status}`);
        }
    } catch (error) {
        console.error('Failed to send Slack notification:', error);
    }
};

export const sendNewUserNotification = async (
    userId: string,
    email: string,
    signupSource: string,
    status:
        | 'pending_verification'
        | 'verified'
        | 'name_set'
        | 'hyper_report_completed' = 'verified',
    name?: string,
    score?: number,
    planTier?: string,
) => {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    let text, header, context;
    if (status === 'hyper_report_completed') {
        text = '📄 User completed HyperFund Report!';
        header = '📄 HyperFund Report Completed';
        context =
            `User has completed their HyperFund report.` +
            (name ? ` Name: *${name}*.` : '') +
            (typeof score === 'number' ? ` Score: *${score}*.` : '') +
            (planTier ? ` Plan: *${planTier}*.` : '');
    } else if (status === 'name_set') {
        text = '✏️ User set their name!';
        header = '✏️ User Set Name';
        context = name
            ? `User has set their name for the first time: *${name}*.`
            : 'User has set their name for the first time.';
    } else if (status === 'verified') {
        text = '✅ User verified their email!';
        header = '✅ User Verified';
        context = 'User has verified their email.';
    } else {
        text = '🕒 New user registered (pending verification)';
        header = '🕒 New User Registration (Pending Verification)';
        context = 'User has not yet verified their email.';
    }

    const message: SlackMessage = {
        text,
        blocks: [
            {
                type: 'header',
                text: {
                    type: 'plain_text',
                    text: header,
                    emoji: true,
                },
            },
            {
                type: 'section',
                fields: [
                    { type: 'mrkdwn', text: `*Email:*\n${email}` },
                    {
                        type: 'mrkdwn',
                        text: `*Signup Source:*\n${signupSource}`,
                    },
                    { type: 'mrkdwn', text: `*User ID:*\n\`${userId}\`` },
                    { type: 'mrkdwn', text: `*Time:*\n${timestamp}` },
                    ...(status === 'name_set' && name
                        ? [{ type: 'mrkdwn', text: `*Name:*\n${name}` }]
                        : []),
                    ...(status === 'hyper_report_completed' && name
                        ? [{ type: 'mrkdwn', text: `*Name:*\n${name}` }]
                        : []),
                    ...(status === 'hyper_report_completed' &&
                    typeof score === 'number'
                        ? [{ type: 'mrkdwn', text: `*Score:*\n${score}` }]
                        : []),
                    ...(status === 'hyper_report_completed' && planTier
                        ? [
                              {
                                  type: 'mrkdwn',
                                  text: `*Plan Tier:*\n${planTier}`,
                              },
                          ]
                        : []),
                ],
            },
            {
                type: 'context',
                elements: [{ type: 'mrkdwn', text: context }],
            },
            { type: 'divider' },
        ],
    };

    await sendSlackNotification(message);
};
